import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { PlatformService } from "../platform/platform.service";
import { GlobalConstants } from "@src/app/global-constants";
import { Noticia } from "@src/app/models/interfaces/noticia.interface";

@Injectable()
export class DetalleNoticiasService {
  private readonly API_URL: any;
  private readonly API_SHARED_URL: any;
  private readonly constantesGlobales: any;
  constructor(
    private readonly httpClient: HttpClient,
    private readonly platformService: PlatformService,
  ) {
    const host = this.platformService.getLocationHostname();
    this.constantesGlobales = new GlobalConstants(host);
    this.API_URL = this.constantesGlobales.urlApi;
    this.API_SHARED_URL = this.constantesGlobales.urlSharedApi;
  }

  /**
   * Obtiene las noticias relacionadas de una noticia en concreto aportando su id.
   * @param {number} idVideo - Id de la noticia.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getNoticiasRelacionadas(idVideo: number): Observable<Noticia[]> {
    if (isNaN(idVideo)) return;
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });

    const body = new HttpParams().set("id_video", String(idVideo));

    return this.httpClient.get<Noticia[]>(this.API_SHARED_URL + "/getNoticiasRelacionadas", { headers: headers, params: body });
  }

  /**
   * Obtiene las noticias pertenecientes a una sección en concreto aportando su id.
   * @param {number} idSeccion - Id de la sección.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getNoticiasSeccionPrensa(idSeccion: number): Observable<Noticia[]> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });

    const body = new HttpParams().set("id_seccion", String(idSeccion));

    return this.httpClient.get<Noticia[]>(this.API_SHARED_URL + "/getVideoUltimasSeccion", { headers: headers, params: body });
  }

  /**
   * Obtiene las noticias anterior y siguiente de una noticia en concreto aportando su id.
   * @param {number} id_video - Id de la noticia.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getNextPrevPrensa(id_video: number): Observable<Noticia[]> {
    if (isNaN(id_video)) return;
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });

    const body = new HttpParams().set("id_video", id_video).set("id_ambito", this.constantesGlobales.idAmbito);

    return this.httpClient.get<Noticia[]>(this.API_SHARED_URL + "/getVideoNextPrev", { headers: headers, params: body });
  }

  /**
   * Obtiene las noticias de una sección en concreto aportando el valor y el tipo de búsqueda. Permite buscar por id o slug de la noticia.
   * @param idAmbito - Id del ámbito del periódico.
   * @param search - Valor por el que se busca.
   * @param type - Tipo (noticia o post).
   * @param by - Por qué se busca (id o slug).
   * @param is_login - Si el usuario está logueado.
   * @param urlFrom - URL de la que proviene.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getVideoPrensa(idAmbito, search, type = "noticia", by = "id", is_login = true, urlFrom = ""): Observable<Noticia[]> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    // const body = {'id_video': id};
    let body = null;
    if (by === "id") {
      body = new HttpParams().set("id_video", search).set("id_ambito", String(idAmbito)).set("is_login", is_login);
    } else {
      body = new HttpParams().set("slug", search).set("id_ambito", String(idAmbito)).set("is_login", is_login);
    }
    if (urlFrom) {
      body = body.set("fromGet", urlFrom);
    }
    let api = this.API_SHARED_URL + "/getVideo";
    if (type === "post") {
      api = this.API_SHARED_URL + "/getPost";
    }
    return this.httpClient.get<Noticia[]>(api, { headers: headers, params: body });
  }

  /**
   * Obtiene el subcanal al que pertenece una noticia aportando el valor de búsqueda y el id del ámbito.
   * El valor de busqueda puede ser el id o el slug de la noticia dependiendo del parámetro "by".
   * @param {string} search - Valor de busqueda.
   * @param {number} idAmbito - Id del ámbito.
   * @param {string} by - Por qué se busca (id o slug).
   * @returns
   */
  public getVideoSubcanal(search: string, idAmbito: number, by: "id" | "slug" = "id"): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    let body = null;
    if (by === "id") {
      body = new HttpParams().set("id_video", search);
    } else {
      body = new HttpParams().set("slug", search);
    }
    body = body.set("id_ambito", idAmbito);
    return this.httpClient.get(this.API_SHARED_URL + "/getVideoSubcanal", { headers: headers, params: body });
  }

  /**
   * Registra una visita para un video específico en un ámbito determinado.
   *
   * @param idVideo - El identificador único del video.
   * @param idAmbito - El identificador único del ámbito.
   * @returns Un observable que emite la respuesta del servidor.
   */
  public registerVisita(idVideo: number, idAmbito: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = new HttpParams().set("id_video", idVideo).set("id_ambito", idAmbito);
    return this.httpClient.get<any>(this.API_URL + "/setVisita", { headers: headers, params: body });
  }

  /**
   * Recupera una noticia a partir del valor de busqueda "search". Este valor puede ser el id o el slug de la noticia.
   * Vale tanto para noticias como para posts.
   * @param idAmbito - Id del ámbito.
   * @param search - Valor de busqueda.
   * @param type - Tipo de noticia (noticia o post).
   * @param by - Por qué se busca (id o slug).
   * @param is_login - Si el usuario está logueado.
   * @param urlFrom - URL de la que proviene.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getVideo(idAmbito, search, type = "noticia", by = "id", is_login = true, urlFrom = ""): Observable<Noticia> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    let body = null;
    if (by === "id") {
      body = new HttpParams().set("id_video", search).set("id_ambito", String(idAmbito)).set("is_login", is_login);
    } else {
      body = new HttpParams().set("slug", search).set("id_ambito", String(idAmbito)).set("is_login", is_login);
    }
    if (urlFrom) {
      body = body.set("fromGet", urlFrom);
    }
    let api = this.API_URL + "/getVideo";
    if (type === "post") {
      api = this.API_URL + "/getPost";
    }
    return this.httpClient.get<Noticia>(api, { headers: headers, params: body });
  }
}
